// Upholstery details of the design, shared by several pages (CSS in app/craft.css).
import { Fragment, type ReactNode } from "react";
import WriteIn from "@/components/WriteIn";
import { contact, d, img } from "@/lib/site";

// Workshop tag (Werkstattzettel): paper job tag with punched hole and string, facts in handwriting, stamp.
export function WorkshopTag({
  rows,
  head = "Werkstattzettel",
  className = "",
  stamp = true,
  write = false,
}: {
  rows: { label: string; value: ReactNode }[];
  head?: string;
  className?: string;
  stamp?: boolean;
  // the values appear letter by letter, as if written by hand (string values only)
  write?: boolean;
}) {
  const total = rows.reduce((n, r) => n + (typeof r.value === "string" ? r.value.length : 0), 0);
  const step = Math.min(32, 2400 / Math.max(total, 1));
  let at = 0;
  const written = (v: string) => (
    <>
      <span className="sr-only">{v}</span>
      <span aria-hidden="true">
        {v.split(" ").map((w, wi) => (
          <Fragment key={wi}>
            {wi > 0 && " "}
            <span className="whitespace-nowrap">
              {[...w].map((c, ci) => (
                <span key={ci} className="wz-ink" data-at={250 + Math.round(at++ * step)}>
                  {c}
                </span>
              ))}
            </span>
          </Fragment>
        ))}
      </span>
    </>
  );
  const tag = (
    <div className={`tag-wrap ${className}`}>
      <div className="tag">
        <span className="tag-hole" />
        <div className="tag-head">{head}</div>
        {rows.map((r, i) => (
          <div
            key={r.label}
            className="tag-row"
            style={i === rows.length - 1 ? { borderBottom: 0, paddingBottom: stamp ? 46 : 0 } : undefined}
          >
            <span className="tag-k">{r.label}</span>
            <span className="tag-v">{write && typeof r.value === "string" ? written(r.value) : r.value}</span>
          </div>
        ))}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        {stamp && <img className="tag-stamp" src={d(img.seal)} alt="Stempel: Raumausstatter-Meisterin Nathalie Netzer" />}
      </div>
      <svg className="tag-string" aria-hidden="true" viewBox="0 0 120 56">
        <path d="M60 50 C 52 34, 70 22, 64 0" fill="none" stroke="#A8834F" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
  return write ? <WriteIn>{tag}</WriteIn> : tag;
}

// Masking tape label with the title in black marker; on gallery tiles it sits on the photo's bottom left.
export function Tape({ children, label, className = "" }: { children: ReactNode; label?: string; className?: string }) {
  return (
    <span className={`ttag-w ${className}`}>
      <span className="ttag">
        {label && <span className="ttag-k">{label}</span>}
        <span className="ttag-v">{children}</span>
      </span>
    </span>
  );
}

// Braid divider: one centred piece of decorative braid.
export function Braid() {
  return (
    <div aria-hidden="true" className="wrap">
      <div className="nails" />
    </div>
  );
}

// Workshop note with brass pin: the address card on Start (contact band) and Kontakt.
export function ContactNote({ className = "" }: { className?: string }) {
  return (
    <div className={`htag-wrap cnote ${className}`}>
      <div className="htag ctag">
        <span className="htag-hole" />
        <div className="tag-head" style={{ textAlign: "left", paddingBottom: 10 }}>
          Werkstattzettel · Kontakt
        </div>
        <div className="tag-row">
          <span className="tag-k">Adresse</span>
          <span className="tag-v">
            Polsterei ideensprung
            <br />
            {contact.street}
            <br />
            {contact.town}
          </span>
        </div>
        <div className="tag-row">
          <span className="tag-k">Telefon</span>
          <a className="tag-v" href={contact.tel}>
            {contact.phone}
          </a>
        </div>
        <div className="tag-row">
          <span className="tag-k">E-Mail</span>
          <a className="tag-v" href={`mailto:${contact.email}`}>
            {contact.email}
          </a>
        </div>
        <div className="tag-row" style={{ borderBottom: 0, paddingBottom: 0 }}>
          <span className="tag-k">Termine</span>
          <span className="tag-v">{contact.hours}</span>
        </div>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className="tag-stamp" src={d(img.seal)} alt="Stempel: Raumausstatter-Meisterin Nathalie Netzer" />
      </div>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img className="htag-pin" src={d(img.pin)} alt="" width={36} height={36} />
    </div>
  );
}
